import { Fill, Icon, Style, Text } from 'ol/style.js';
import { asString, fromString } from 'ol/color';

import { SketchObjectType } from 'cadenza/map/classic-sketch-layer/sketch-object-type';
import { getImageObjectRenderer } from 'cadenza/map/geom/image-object-renderer';
import { toCssFontSize } from 'cadenza/utils/fonts';
import { createStyle } from 'cadenza/map/style/create-style';
import { FillSignatureType, PointSignatureType, StyleProperty } from 'cadenza/map/style/style-constants';
import { GeometryType } from 'cadenza/map/geom/geometry-type';

import { createSketchStrategy as sketchItemStrategy } from './strategy';

export const modelStyleToOpenLayersStyle = sketchItemStrategy({

  [SketchObjectType.IMAGE]: item => {
    const image = new Icon({
      src: item.image || item.style[StyleProperty.IMAGE_AS_BASE64]
    });
    image.load();
    const renderer = getImageObjectRenderer(image.getImage(1));
    return new Style({ renderer });
  },

  [SketchObjectType.LINE]: item => lineModelStyleToOlStyle(item.style),

  [SketchObjectType.OVAL]: item => polygonStyleToOlStyle(item.style, GeometryType.OVAL),

  [SketchObjectType.POINT]: item => pointStyleToOlStyle(item.style),

  [SketchObjectType.POLYGON]: item => polygonStyleToOlStyle(item.style, GeometryType.POLYGON),

  [SketchObjectType.POLYLINE]: item => lineModelStyleToOlStyle(item.style),

  [SketchObjectType.RECTANGLE]: item => polygonStyleToOlStyle(item.style, GeometryType.RECTANGLE),

  [SketchObjectType.TEXT]: item => {
    const textAlign = getTextAlign(item.style.anchor);
    return new Style({
      text: new Text({
        backgroundFill: new Fill({ color: item.style.backgroundVisible ? item.style.backgroundColor : changeTransparency(item.style.backgroundColor, 0) }),
        font: getFontStyle(item.style),
        text: item.lines ? item.lines.join('\n') : '',
        fill: new Fill({ color: item.style[StyleProperty.FOREGROUND_COLOR] }),
        textBaseline: textAlign[0],
        textAlign: textAlign[1],
        placement: 'point',
        rotation: -convertToOl5Rotation(item.style.rotation), // we take a negative value of the angle to match Cadenza Desktop behavior
        padding: [ 2, 0, 2, 0 ]
      })
    });
  }

});

function lineModelStyleToOlStyle (itemStyle) {
  const styleConfiguration  = {
    [StyleProperty.FOREGROUND_COLOR]: itemStyle[StyleProperty.FOREGROUND_COLOR],
    [StyleProperty.LINE_WIDTH]: itemStyle[StyleProperty.LINE_WIDTH],
    [StyleProperty.LINE_START_DECORATION]: itemStyle[StyleProperty.LINE_START_DECORATION],
    [StyleProperty.LINE_END_DECORATION]: itemStyle[StyleProperty.LINE_END_DECORATION]
  };

  return createStyle(styleConfiguration, { geometryType: GeometryType.LINE });
}

function pointStyleToOlStyle (itemStyle) {
  const styleConfiguration = {
    [StyleProperty.POINT_SIGNATURE_TYPE]: itemStyle[StyleProperty.POINT_SIGNATURE_TYPE],
    [StyleProperty.IMAGE_AS_BASE64]: itemStyle[StyleProperty.IMAGE_AS_BASE64],
    [StyleProperty.ROTATION]: itemStyle[StyleProperty.ROTATION],
    [StyleProperty.FOREGROUND_COLOR]: itemStyle[StyleProperty.FOREGROUND_COLOR],
    [StyleProperty.SYMBOL_SIZE]: isRenderedAsPointImage(itemStyle) ? null : itemStyle.signatureSize,
    [StyleProperty.LINE_WIDTH]: itemStyle[StyleProperty.LINE_WIDTH],
    [StyleProperty.BORDER_COLOR]: itemStyle[StyleProperty.BORDER_COLOR]
  };

  return createStyle(styleConfiguration, { geometryType: GeometryType.POINT });
}

function polygonStyleToOlStyle (itemStyle, geometryType) {
  let styleConfiguration;
  if (itemStyle[StyleProperty.FILL_SIGNATURE_TYPE] === FillSignatureType.NORMAL) {
    styleConfiguration = {
      [StyleProperty.FOREGROUND_COLOR]: itemStyle[StyleProperty.FOREGROUND_COLOR],
      [StyleProperty.BACKGROUND_COLOR]: itemStyle[StyleProperty.BACKGROUND_COLOR],
      [StyleProperty.LINE_WIDTH]: itemStyle[StyleProperty.LINE_WIDTH],
      [StyleProperty.BORDER_COLOR]: itemStyle[StyleProperty.BORDER_COLOR],
      [StyleProperty.FILL_STYLE]: itemStyle[StyleProperty.FILL_STYLE]
    };
  } else {
    styleConfiguration  = {
      [StyleProperty.BORDER_COLOR]: itemStyle[StyleProperty.BORDER_COLOR],
      [StyleProperty.LINE_WIDTH]: itemStyle[StyleProperty.LINE_WIDTH],
      [StyleProperty.FILL_SIGNATURE_TYPE]: itemStyle[StyleProperty.FILL_SIGNATURE_TYPE],
      [StyleProperty.ROTATION]: itemStyle[StyleProperty.ROTATION],
      [StyleProperty.IMAGE_AS_BASE64]: itemStyle[StyleProperty.IMAGE_AS_BASE64]
    };
  }

  return createStyle(styleConfiguration, { geometryType });
}

function convertToOl5Rotation (itemRotation) {
  return itemRotation * Math.PI / 180;
}

function getFontStyle (style) {
  const fontStyle = [];
  if (style.bold) {
    fontStyle.push('bold');
  }
  if (style.italic) {
    fontStyle.push('italic');
  }
  fontStyle.push(toCssFontSize(style.fontSize));
  fontStyle.push(style.fontFamily);
  return fontStyle.join(' ');
}

const anchorMapping = {
  NORTH_WEST: [ 'top', 'left' ],
  NORTH: [ 'top', 'center' ],
  NORTH_EAST: [ 'top', 'right' ],
  WEST: [ 'alphabetic', 'left' ],
  CENTER: [ 'alphabetic', 'center' ],
  EAST: [ 'alphabetic', 'right' ],
  SOUTH_WEST: [ 'bottom', 'left' ],
  SOUTH: [ 'bottom', 'center' ],
  SOUTH_EAST: [ 'bottom', 'right' ]
};

function getTextAlign (anchor) {
  return anchorMapping[anchor];
}

export function changeTransparency (color, transparency) {
  const colorArray = fromString(color).slice();
  colorArray[3] = transparency;
  return asString(colorArray);
}

/**
 * In sketch we need to add support for style to be either a function, or an array of styles, besides the existing style object.
 * It has been decided that the first style in the array (if the feature style is an array) will be the most meaningful style:
 * the main style/the feature style, the rest are just styles which open layers renders additionally to the main one.
 *
 * This function returns the main style.
 *
 * @param {object} feature - the feature whose style we want to get
 * @return {object} the base style
 */
export function getMainStyle (feature) {
  let style = feature.getStyle();
  if (typeof style === 'function') {
    style = style(feature);
  }
  if (Array.isArray(style)) {
    style = style[0];
  }
  return style;
}

/**
 * Creates a styleItem from a paletteItem. Note that the style property of the styleItem are cloned,
 * so that modifying them, don't affect more than 1 styleItem based on the same paletteItem.
 *
 * @param {object} paletteItem - Additional options.
 * @param {SketchObjectType} [paletteItem.type] - The sketch object type which can be drawn using this paletteItem.
 * @param {string} [paletteItem.style] - The style of the paletteItem
 * @param {string} [paletteItem.image] - The image as base64 for certain paletteItems
 * @return {object} a new object containing the type and the cloned style, optionally it can contain the image as well
 */
export function createStyleItemFromPaletteItem ({ type, style, image } = {}) {
  return { type, style: { ...style }, ...(image ? { image } : {}) };
}

/**
 * Returns true if the itemStyle will be rendered as an image.
 *
 * @param {object} itemStyle - The item style
 * @return {boolean} - true if the item will be rendered as an image, false otherwise
 */
export function isRenderedAsPointImage (itemStyle) {
  return itemStyle[StyleProperty.POINT_SIGNATURE_TYPE] === PointSignatureType.IMAGE
    || itemStyle[StyleProperty.POINT_SIGNATURE_TYPE] === PointSignatureType.CHARACTER;
}

/**
 * Gets the opacity from a color
 *
 * @param {string|Array<number>} color - as rgba string or array of rgba components
 * @return {number} - The opacity of the color
 */
export function getOpacity (color) {
  if (typeof color === 'string') {
    color = fromString(color).slice();
  }
  return (color.length === 4) ? color[3] : 1;
}
